import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../Messages';
import { showApiError } from '../../utils/errorUtils';

const TaskSplitDialog = ({ isOpen, onClose, task, onSplitComplete }) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError } = useMessages();

  const [loading, setLoading] = useState(false);
  const [numberOfSplits, setNumberOfSplits] = useState(2);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (numberOfSplits < 2 || numberOfSplits > 5) {
      showError(t('taskSplit.errors.invalidSplits'), t('taskSplit.errors.invalidSplitsDesc'));
      return;
    }

    setLoading(true);
    try {
      const equalRatio = 1.0 / numberOfSplits;
      const splitRequest = {
        task_id: task.task_id,
        split_reason: 'URGENT',
        split_configs: Array(numberOfSplits).fill(null).map(() => ({
          split_ratio: equalRatio,
          machine_id: null,
          assigned_to: null,
          target_completion: null,
          notes: null
        })),
        split_notes: `Task split into ${numberOfSplits} equal parts`
      };

      const response = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${task.task_id}/split`), {
        method: 'POST',
        body: JSON.stringify(splitRequest)
      });

      if (response.ok) {
        const result = await response.json();
        showSuccess(
          t('taskSplit.messages.splitSuccess'),
          t('taskSplit.messages.splitSuccessDesc', { count: result.total_splits })
        );
        onSplitComplete(result);
        onClose();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error splitting task:', error);
      showError(t('taskSplit.errors.splitFailed'), t('taskSplit.errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog-content" onClick={e => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{t('taskSplit.title')}</h2>
          <button className="dialog-close" onClick={handleClose} disabled={loading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dialog-body">
            <div className="task-info-section">
              <h3>{t('taskSplit.sections.taskInfo')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('taskSplit.fields.taskCode')}:</label>
                  <span>{task.task_code}</span>
                </div>
                <div className="info-item">
                  <label>{t('taskSplit.fields.taskName')}:</label>
                  <span>{task.task_name}</span>
                </div>
                <div className="info-item">
                  <label>{t('taskSplit.fields.currentStatus')}:</label>
                  <span className={`status-badge status-${task.status?.toLowerCase()}`}>
                    {task.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="split-config-section">
              <h3>{t('taskSplit.sections.splitConfiguration')}</h3>
              <p>{t('taskSplit.equalSplitHint')}</p>

              <div className="form-group">
                <label>{t('taskSplit.fields.numberOfSplits')}:</label>
                <select
                  value={numberOfSplits}
                  onChange={(e) => setNumberOfSplits(parseInt(e.target.value))}
                  required
                >
                  <option value={2}>{t('taskSplit.splitOption', { count: 2, percent: 50 })}</option>
                  <option value={3}>{t('taskSplit.splitOption', { count: 3, percent: 33 })}</option>
                  <option value={4}>{t('taskSplit.splitOption', { count: 4, percent: 25 })}</option>
                  <option value={5}>{t('taskSplit.splitOption', { count: 5, percent: 20 })}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="dialog-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              {t('taskSplit.buttons.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? t('taskSplit.buttons.splitting') : t('taskSplit.buttons.splitInto', { count: numberOfSplits })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskSplitDialog;